import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalWindow from "../../shared/ModalWindow";

import style from "./style.module.scss";
import OrdersSectionItemInfoColumn from "../section-item/components/OrdersSectionItemInfoColumn";
import { Order } from "../../../services";
import cn from "classnames";
import { toast } from "react-toastify";
import Loading from "../../shared/Loading/Loading";
import { requestErrorDisplay } from "../../../utils";
import CustomInput from "../../custom/custom-input/CustomInput";
import CustomPhoneInput from "../../custom/custom-phone-input/CustomPhoneInput";
import { getCountryById } from "../../../utils/countryPhoneData";

const onlyDigits = (value) => (value ? String(value).replace(/\D/g, "") : "");

const OrdersLocationEditModal = ({
  item,
  onClose,
  updateContent,
  titleEnd = "",
  isSender = false,
  isRailway = false,
}) => {
  const { t } = useTranslation();
  const locationContext = isRailway ? "railway" : undefined;

  const [name, setName] = useState(item?.name || null);
  const [address, setAddress] = useState(item?.fullAddress || null);
  const [phone, setPhone] = useState(onlyDigits(item?.phoneNumber));
  const [email, setEmail] = useState(item?.email || null);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const phoneCountry = getCountryById(item?.countryId, t);

  useEffect(() => {
    setName(item?.name || null);
    setAddress(item?.fullAddress || null);
    setPhone(onlyDigits(item?.phoneNumber));
    setEmail(item?.email || null);
  }, [item]);

  const validationData = () => {
    const newErrors = {};
    let allow = true;

    if (!name) {
      toast.error(t("ordersLocationAddModal.validation.recipientNameRequired"));
      newErrors.name = t("ordersLocationAddModal.validation.enterName");
      allow = false;
    }
    if (!address) {
      toast.error(t("ordersLocationAddModal.validation.addressRequired", { context: locationContext }));
      newErrors.address = t("ordersLocationAddModal.validation.specifyAddress", { context: locationContext });
      allow = false;
    }

    setErrors(newErrors);
    return allow;
  };

  const submitHandle = () => {
    if (!validationData()) {
      return;
    }

    const formData = {
      id: item.id,
      type: item.type,
      name: name,
      countryId: item.countryId,
      cityId: item.cityId,
      phoneNumber: phone ? String(phone).replace("+", "") : null,
      email: email,
      fullAddress: address,
      latLng: item.latLng,
    };

    setLoading(true);
    Order.updateUserAddress(item.id, formData)
      .then((res) => res.data)
      .then((response) => {
        toast.info(t("ordersLocationAddModal.toast.updateSuccess", { context: locationContext }));
        updateContent(response || { ...item, ...formData });
      })
      .catch((error) => {
        requestErrorDisplay(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <ModalWindow onClose={onClose}>
      <div className={style.container}>
        <div className={style.modal} style={{ flex: 2 }}>
          <h3>
            {t("ordersLocationAddModal.editTitle", { context: locationContext })} {titleEnd}
          </h3>
          <OrdersSectionItemInfoColumn>
            <CustomInput
              labelText={
                isSender ? t("addressOrder.organizationName") : t("ordersLocationAddModal.recipientNameLabel")
              }
              placeholder={
                isSender
                  ? t("addressOrder.enterOrganizationName")
                  : t("ordersLocationAddModal.recipientNamePlaceholder")
              }
              isBgGray
              value={name}
              onChangeValue={setName}
              errorText={errors?.name}
            />
          </OrdersSectionItemInfoColumn>
          <OrdersSectionItemInfoColumn>
            <CustomInput
              labelText={t("ordersLocationAddModal.addressLabel", { context: locationContext })}
              placeholder={t("ordersLocationAddModal.addressPlaceholder", { context: locationContext })}
              isBgGray
              value={address}
              onChangeValue={setAddress}
              errorText={errors?.address}
            />
          </OrdersSectionItemInfoColumn>
          <OrdersSectionItemInfoColumn>
            {!item?.phoneNumber && phoneCountry ? (
              <CustomPhoneInput
                labelText={t("ordersLocationAddModal.phoneLabel")}
                placeholder={t("ordersLocationAddModal.phonePlaceholder")}
                value={phone}
                onChangeValue={setPhone}
                errorText={errors?.phone}
                defaultCountry={phoneCountry.code}
                isBgGray
              />
            ) : (
              <CustomInput
                labelText={t("ordersLocationAddModal.phoneLabel")}
                placeholder={t("ordersLocationAddModal.phonePlaceholder")}
                inputType="text"
                isBgGray
                maxLength={15}
                value={phone}
                onChangeValue={(value) => setPhone(onlyDigits(value))}
                errorText={errors?.phone}
              />
            )}
          </OrdersSectionItemInfoColumn>
          <OrdersSectionItemInfoColumn>
            <CustomInput
              labelText={t("ordersLocationAddModal.emailLabel")}
              placeholder={t("ordersLocationAddModal.emailPlaceholder")}
              inputType="text"
              onChangeValue={setEmail}
              value={email}
              errorText={errors?.email}
              isBgGray
            />
          </OrdersSectionItemInfoColumn>
          <button type="button" className={cn(style.modal_button)} onClick={submitHandle}>
            {isLoading
              ? <Loading inBlock marginBottom={0} />
              : t("ordersLocationAddModal.buttons.save", { context: locationContext })}
          </button>
        </div>
      </div>
    </ModalWindow>
  );
};

export default OrdersLocationEditModal;
